import { array } from "valibot"

import { BaseApiService, KyOptions } from "../../../../apps/base/src/composables/services/api.service"
import {
  autocompleteItemSchema,
  getCurrentQuestionnairePageResponseSchema,
  getNextPrevPageResponseSchema,
  getQuestionnaireProgressSchema,
  getQuestionnaireResponseSchema,
  sendAnswersResponseSchema,
} from "./kyc-api-responses"

export type KycApiParams = {
  questionnaire?: string
  chapter?: string
  page?: string
  phase?: number
}

export type KycFetchKycAnswersParams = { questions: string[] }

class KycServiceRaw extends BaseApiService {
  getNextOrPrevPage = async (
    direction: "next" | "prev" = "next",
    data?: KycApiParams,
    config: KyOptions = {}
  ) => {
    const { questionnaire, chapter, page, phase } = data || {}
    if (!questionnaire) return Promise.reject("Questionnaire ID is required")

    return this.instance
      .get(`/kyc/questionnaires/${questionnaire.toUpperCase()}/phases/${phase}/${direction}-page?`, {
        ...config,
        params: { ...config.params, chapter, page, phase },
      })
      .then(this.getData)
      .then((res) => this.parse(res, getNextPrevPageResponseSchema))
  }

  getNextPage = async (data?: KycApiParams, config: KyOptions = {}) =>
    this.getNextOrPrevPage("next", data, config)

  getPrevPage = async (data?: KycApiParams, config: KyOptions = {}) =>
    this.getNextOrPrevPage("prev", data, config)

  getQuestionnaireProgress = async (
    data?: { questionnaire: string; phase?: number },
    config: KyOptions = {}
  ) => {
    const { questionnaire, phase } = data || {}
    if (!questionnaire) return Promise.reject("Questionnaire ID is required")

    return this.instance
      .get(`/kyc/questionnaires/${questionnaire}/phases/${phase}/progress`, config)
      .then(this.getData)
      .then((data) => {
        return this.parse(data, array(getQuestionnaireProgressSchema))
      })
  }

  sendAnswers = async (
    data: {
      questionnaire: string
      chapter: string
      page: string
      phase: number
      isNext: boolean
      answers: Record<string, any>
    },
    config: KyOptions = {}
  ) => {
    return this.instance
      .post(`/kyc/answers`, data, config)
      .then(this.getData)
      .then((data) => this.parse(data, sendAnswersResponseSchema))
  }

  getQuestionnaireAnswers = async (config: KyOptions = {}) =>
    this.instance
      .get(`/kyc/answers`, config)
      .then(this.getData)
      .then((data) => data.answers)

  autocomplete = async (
    data: { lib: string; searchPattern?: string },
    config: KyOptions<{ lang?: string } & PaginationParams> = {}
  ) => {
    const { lib, searchPattern = "" } = data || {}
    if (!lib) return Promise.reject("Question Field is required")

    return this.instance.get(`/kyc/autocomplete/${lib}/${searchPattern}`, config).then((res) => {
      return this.parsePaginated(res.data, array(autocompleteItemSchema))
    })
  }

  getCurrentQuestionnairePage = async (
    data: {
      questionnaire: string
      phase: number
      chapter?: string
    },
    config: KyOptions = {}
  ) => {
    const { questionnaire, phase, chapter } = data
    return this.instance
      .get(`/kyc/questionnaires/${questionnaire}/phases/${phase}/current-page`, {
        ...config,
        params: { chapter },
      })
      .then(this.getData)
      .then((data) => this.parse(data, getCurrentQuestionnairePageResponseSchema))
  }

  autocompleteRecommendations = async (data: { lib: string; size?: number }, config: KyOptions = {}) => {
    const { lib, size } = data
    if (!lib) return Promise.reject("Autocomplete lib is missing")

    return this.instance
      .get(`/kyc/autocomplete/${lib}/recommendations`, { ...config, params: { ...config.params, size } })
      .then(this.getData)
      .then((data) => {
        return this.parse(data, array(autocompleteItemSchema))
      })
  }
}

/** Exporting a singleton object to entire app */
export const KycService = new KycServiceRaw()
